import React from "react";
import { AUTH_PROVIDER } from "../lib/auth-provider";

const AuthContext = React.createContext();

// Provider-specific session sources, loaded only for the active provider.
let firebase, auth, authClient, cacheUser;

if (AUTH_PROVIDER === "firebase") {
  ({ firebase, auth } = await import('../lib/firebase'));
} else if (AUTH_PROVIDER === "better-auth") {
  ({ authClient } = await import('../lib/better-auth'));
  ({ cacheUser } = await import('./better-auth-user'));
}

// Mirror Firebase's verified-email gate when requested. The server enforces it
// via BETTER_AUTH_REQUIRE_EMAIL_VERIFICATION; this flag only drives SPA routing.
const requireVerification = process.env.REACT_APP_REQUIRE_EMAIL_VERIFICATION === "true";

function FirebaseAuthProvider(props) {
  const [status, setStatus] = React.useState("loading");
  React.useEffect(() => {
    // onIdTokenChanged is called when Firebase's logged in state changes:
    // a user means we're logged in, otherwise we're not.
    const unsubscribe = firebase.onIdTokenChanged(auth, (user) => {
      let s = user ? "unverified" : "loggedOut";
      setStatus(user?.emailVerified ? "loggedIn" : s);
    });
    return unsubscribe;
  }, []);
  return <AuthContext.Provider {...props} value={status} />;
}

function BetterAuthProvider(props) {
  const { data: session, isPending } = authClient.useSession();
  // Keep the synchronous getCurrentUser() snapshot (used by useUser()) in sync.
  React.useEffect(() => { cacheUser && cacheUser(session?.user); }, [session]);
  let status;
  if (isPending) status = "loading";
  else if (!session) status = "loggedOut";
  else if (requireVerification && !session.user?.emailVerified) status = "unverified";
  else status = "loggedIn";
  return <AuthContext.Provider {...props} value={status} />;
}

function NullAuthProvider(props) {
  return <AuthContext.Provider {...props} value="loggedIn" />;
}

const AuthProvider =
  AUTH_PROVIDER === "null" ? NullAuthProvider
    : AUTH_PROVIDER === "better-auth" ? BetterAuthProvider
      : FirebaseAuthProvider;

function useAuth() {
  const status = React.useContext(AuthContext);
  if (status === undefined)
    throw new Error("useAuth must be called within an AuthProvider");
  return status;
}

export { AuthProvider, useAuth };
